import { appLocale } from "../../locales/display.ts";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayTime } from "src/utils/dateTime";
import { CursorExportPopup } from "../../modules/cursorExport/CursorExportPopup";
import type { CursorExportJob } from "../../modules/cursorExport/cursorExport";
import { sessionExportRequestData, parseSessionExportPage } from "../../services/sessionCsvExport";
import Button from "@ui/button/Button";
import Signal from "badmfck-signal";
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Send } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import type { IPaymentSession } from "src/types/types";
import { Pagination } from "../conponents/Pagination";
import { toDate, type TTone } from "../providers/providersData";
import {
	Cell,
	CopyButton,
	DirectoryCard,
	DirectoryRow,
	EmptyState,
	EntityCell,
	FilterChips,
	FilterSelect,
	LinkButton,
	Mono,
	Muted,
	Notice,
	RowChevron,
	SearchField,
	SkeletonRows,
	StatusPill,
	TabIntro,
	TabSurface,
} from "../providers/providersUi";

/** Fired after an adjustment or a payout is created, so the list reads itself again. */
export const S_ADJUSTMENTS_CHANGED = new Signal<void>("S_ADJUSTMENTS_CHANGED");

const PER_PAGE = 20;

type TKind = "adjustment_in" | "adjustment_out" | "oct";
type TFilter = "all" | "in" | "out" | "payouts";

const KINDS: Record<TKind, { label: string; sign: "+" | "−"; className: string; icon: ReactNode }> = {
	adjustment_in: { get label() { return msg("adjustmentIn_dc6ff320"); }, sign: "+", className: "in", icon: <ArrowDownLeft /> },
	adjustment_out: { get label() { return msg("adjustmentOut_b0935d07"); }, sign: "−", className: "out", icon: <ArrowUpRight /> },
	oct: { get label() { return msg("payout"); }, sign: "−", className: "payout", icon: <Send /> },
};

const FILTER_TYPES: Record<Exclude<TFilter, "all">, TKind[]> = {
	in: ["adjustment_in"],
	out: ["adjustment_out"],
	payouts: ["oct"],
};

const STATUS_TONES: Record<string, TTone> = {
	completed: "positive",
	pending: "warning",
	processing: "warning",
	expired: "warning",
	failed: "negative",
	error: "negative",
	created: "neutral",
	canceled: "neutral",
	cancelled: "neutral",
};

const PERIODS = [
	{ value: "7", get label() { return msg("last7Days"); } },
	{ value: "30", get label() { return msg("last30Days"); } },
	{ value: "90", get label() { return msg("last90Days"); } },
	{ value: "365", get label() { return msg("last12Months"); } },
];

const dayParam = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

type TPagination = { total: number; page: number; perPage: number; totalPages: number };

const itemsOf = (data: any): IPaymentSession[] => (Array.isArray(data?.items) ? data.items : Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : []);

const paginationOf = (data: any, page: number, count: number): TPagination | undefined => {
	if (data?.pagination) return data.pagination;
	if (!data) return undefined;
	const total = Number(data.total ?? count) || 0;
	return { total, page: data.page ?? page, perPage: PER_PAGE, totalPages: Math.ceil(total / PER_PAGE) };
};

/** Minor units to "1,500.00". */
const money = (amount: unknown) => (Number(amount) / 100).toLocaleString(appLocale("en-US"), { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const csvCell = (value: unknown) => {
	const text = value === null || value === undefined ? "" : String(value);
	return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** The whole tab scrolls, as on Statistics and Fees; the pager stays pinned to the bottom of the tab. */
const Layout = styled.div`
	display: flex;
	flex-direction: column;
	/* At least the tab's height, so a short list still keeps the pager at the bottom. */
	flex: 1 0 auto;
	gap: 0;
	.list {
		margin-bottom: 15px;
	}
	> [data-panel="footer"] {
		position: sticky;
		bottom: 0;
		z-index: 3;
		margin-top: auto;
	}
	.kind {
		display: inline-grid;
		place-items: center;
		flex: 0 0 34px;
		width: 34px;
		height: 34px;
		border: 1px solid;
		border-radius: 9px;
	}
	.kind svg {
		width: 17px;
		height: 17px;
	}
	.kind.in {
		border-color: #abefc6;
		background: #ecfdf3;
		color: #067647;
	}
	.kind.out {
		border-color: #fecdca;
		background: #fef3f2;
		color: #b42318;
	}
	.kind.payout {
		border-color: #d9d6fe;
		background: #f4f3ff;
		color: #5925dc;
	}
	.amount {
		display: block;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		text-align: right;
		white-space: nowrap;
	}
	.amount.in {
		color: #067647;
	}
	.amount.out,
	.amount.payout {
		color: #b42318;
	}
	.amount .currency {
		margin-left: 4px;
		color: #667085;
		font-weight: 400;
	}
	.right {
		display: block;
		text-align: right;
	}
	.ref {
		min-width: 0;
	}
	.ref-line {
		display: inline-flex;
		align-items: center;
		gap: 2px;
	}
	.ref-sub {
		overflow: hidden;
		color: #667085;
		font-size: 12px;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.footer-actions {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-left: auto;
	}
`;

/**
 * Manual corrections of a website's balance and, when it has an OCT channel, its card payouts. The tab scrolls as a
 * whole above the shared pager bar; a row opens the operation like any payment.
 */
export const MerchantAdjustments = ({ merchantId, showPayouts, actions }: { merchantId: number; showPayouts: boolean; actions?: ReactNode }) => {
	useLocale();
	const [filter, setFilter] = useState<TFilter>("all");
	const [period, setPeriod] = useState("");
	const [query, setQuery] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [revision, setRevision] = useState(0);
	const [state, setState] = useState<{ key: string; response?: IAPIResponse } | null>(null);
	const [counts, setCounts] = useState<Partial<Record<TFilter, number>>>({});
	const [csvExportJob, setCsvExportJob] = useState<CursorExportJob<IPaymentSession> | null>(null);
	const downloading = !!csvExportJob;

	S_ADJUSTMENTS_CHANGED.use([], () => setRevision(value => value + 1));
	GD.S_TRANSACTIONS_UPDATE.use([], () => setRevision(value => value + 1));

	// A typed search waits for a short pause before it is sent.
	useEffect(() => {
		const timer = window.setTimeout(() => setSearch(query.trim()), 400);
		return () => window.clearTimeout(timer);
	}, [query]);
	useEffect(() => setPage(0), [filter, period, search]);

	const allTypes: TKind[] = showPayouts ? ["adjustment_in", "adjustment_out", "oct"] : ["adjustment_in", "adjustment_out"];
	const requestData = (types: TKind[], nextPage: number, perPage = PER_PAGE) => {
		const data: Record<string, unknown> = { target_id: merchantId, target_type: "merchant", type: types, pagination: { page: nextPage, perPage } };
		if (search) data.search = search;
		else if (period) {
			const from = new Date();
			from.setDate(from.getDate() - Number(period));
			data.from = dayParam(from);
			data.to = dayParam(new Date());
		}
		return data;
	};
	const types = filter === "all" ? allTypes : FILTER_TYPES[filter];
	const key = JSON.stringify([merchantId, types, page, period, search, revision]);

	useEffect(() => {
		let active = true;
		void GD.REQ_API.request({ method: "session/list", data: requestData(types, page) }).then(response => {
			if (active) setState({ key, response });
		});
		return () => {
			active = false;
		};
	}, [key]);

	// Counts for the type chips: one-row requests, so only the totals travel.
	useEffect(() => {
		let active = true;
		const filters: Exclude<TFilter, "all">[] = showPayouts ? ["in", "out", "payouts"] : ["in", "out"];
		void Promise.all(
			filters.map(async item => {
				const response = await GD.REQ_API.request({ method: "session/list", data: requestData(FILTER_TYPES[item], 0, 1) });
				return [item, response.error ? undefined : paginationOf(response.data, 0, itemsOf(response.data).length)?.total] as const;
			}),
		).then(entries => {
			if (!active) return;
			const next: Partial<Record<TFilter, number>> = Object.fromEntries(entries);
			next.all = entries.every(([, value]) => value !== undefined) ? entries.reduce((sum, [, value]) => sum + (value ?? 0), 0) : undefined;
			setCounts(next);
		});
		return () => {
			active = false;
		};
	}, [merchantId, showPayouts, period, search, revision]);

	const response = state?.response;
	const loading = state?.key !== key;
	const items = response && !response.error ? itemsOf(response.data) : [];
	const pagination = response && !response.error ? paginationOf(response.data, page, items.length) : undefined;
	const filtered = filter !== "all" || !!period || !!search;

	const downloadCsv = () => {
		if (csvExportJob) return;
		const data = requestData(types, 0);
		setCsvExportJob({
			filename: `adjustments_merchant_${merchantId}_${dayParam(new Date())}.csv`,
			loadPage: async (cursor, signal) => {
				const result = await GD.REQ_API.request({ method: "session/list", signal, data: sessionExportRequestData(data, cursor) });
				if (result.error) throw new Error(result.error.message || msg("couldNotLoadTransactions"));
				return parseSessionExportPage<IPaymentSession>(result.data);
			},
			getRowKey: row => row.id,
			buildFile: rows => {
				if (!rows.length) throw new Error(msg("noTransactionsToExport"));
				const columns = ["id", "created_at", "type", "status", "amount", "currency", "payment_id", "email"] as const;
				const lines = rows.map(row =>
					columns
						.map(column => {
							if (column === "amount") return csvCell((Number(row.amount) / 100).toFixed(2));
							if (column === "created_at") return csvCell(toDate(row.created_at)?.toISOString() ?? row.created_at);
							return csvCell((row as any)[column]);
						})
						.join(","),
				);
				return new Blob(["﻿" + [columns.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
			},
		});
	};

	const filterOptions = [
		{ id: "all" as const, label: msg("all"), count: counts.all },
		{ id: "in" as const, label: msg("in"), count: counts.in },
		{ id: "out" as const, label: msg("out"), count: counts.out },
		...(showPayouts ? [{ id: "payouts" as const, label: msg("payouts"), count: counts.payouts }] : []),
	];

	return (
		<Layout>
			{csvExportJob && <CursorExportPopup job={csvExportJob} title={msg("exportAdjustments")} description={msg("merchantAdjustmentsCsv")} onClose={() => setCsvExportJob(null)} />}
			<TabSurface style={{ paddingTop: 0, flex: "0 0 auto" }}>
				<TabIntro
					text={
						showPayouts
							? msg("manualCorrectionsOfTheWebsiteBalanceAndCardPayoutsAnAdjustme_37ac2bda")
							: msg("manualCorrectionsOfTheWebsiteBalanceAnAdjustmentInCreditsItA_f672854f")
					}
					actions={actions}
				/>
			</TabSurface>
			{response?.error && <Notice tone="error">{response.error.message ?? msg("couldNotLoadAdjustments")}</Notice>}
			<div className="list">
				<DirectoryCard
					stickyHead
					columns="minmax(240px, 1fr) minmax(160px, max-content) minmax(120px, max-content) minmax(110px, max-content)"
					head={["Operation", "Reference", <span className="right">{"Amount"}</span>, "Status"]}
					toolbar={
						<>
							<SearchField value={query} onChange={setQuery} placeholder={msg("searchByIdOrEmail")} />
							<FilterChips label={msg("operationType")} value={filter} onChange={setFilter} options={filterOptions} />
							<span className="grow" />
							<FilterSelect label={msg("allTime")} value={period} onChange={setPeriod} options={PERIODS} />
						</>
					}
					footer={
						filtered &&
						!loading && (
							<>
								<span>{pagination ? msg("templateFound_ed9a2c80", { v0: pagination.total }) : ""}</span>
								<LinkButton
									onClick={() => {
										setFilter("all");
										setPeriod("");
										setQuery("");
									}}>{msg("clearFilters")}</LinkButton>
							</>
						)
					}>
					{loading && !items.length ? (
						<SkeletonRows rows={4} />
					) : !items.length ? (
						!response?.error && (
							<EmptyState
								icon={<ArrowLeftRight size={20} />}
								title={filtered ? msg("nothingMatchesTheFilters") : msg("noAdjustmentsYet")}
								text={
									filtered
										? msg("tryAnotherPeriodOrTypeOrClearTheFilters")
										: msg("correctionsOfTheWebsiteBalanceAppearHereWithTheirStatusOnceYouCreateOne")
								}
							/>
						)
					) : (
						items.map(row => {
							const kind = KINDS[row.type as TKind] ?? { label: row.type, sign: "" as const, className: "", icon: <ArrowLeftRight /> };
							const date = toDate(row.created_at);
							const status = String(row.status ?? "").toLowerCase();
							return (
								<DirectoryRow key={row.id} label={msg("templateOpen_c693ba15", { v0: kind.label, v1: row.id })} onOpen={() => GD.S_TRANSACTION_PREVIEW.invoke(row)}>
									<Cell kind="main">
										<EntityCell
											icon={
												<span className={`kind ${kind.className}`} aria-hidden="true">
													{kind.icon}
												</span>
											}
											name={kind.label}
											sub={
												<>
													<span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
														<Mono>#{row.id}</Mono>
														<CopyButton value={String(row.id)} label={msg("copyId")} />
													</span>
													{date && (
														<span>
															<DateTimeTooltip value={date}>{formatDisplayDate(date, { locale: "en-US", format: { month: "short", day: "2-digit", year: "numeric" }, withZone: false })}</DateTimeTooltip>,{" "}
															<DateTimeTooltip value={date}>{formatDisplayTime(date, { format: { hour: "2-digit", minute: "2-digit" } })}</DateTimeTooltip>
														</span>
													)}
												</>
											}
										/>
									</Cell>
									<Cell label={msg("reference")}>
										{row.payment_id || row.email ? (
											<div className="ref">
												{row.payment_id ? (
													<span className="ref-line">
														<Mono>{row.payment_id}</Mono>
														<CopyButton value={String(row.payment_id)} label={msg("copyPaymentId")} />
													</span>
												) : null}
												{row.email && <div className="ref-sub">{row.email}</div>}
											</div>
										) : (
											<Muted>—</Muted>
										)}
									</Cell>
									<Cell label={msg("amount")}>
										<span className={`amount ${kind.className}`}>
											{kind.sign}
											{money(row.amount)}
											<span className="currency">{row.currency}</span>
										</span>
									</Cell>
									<Cell kind="trail">
										<StatusPill tone={STATUS_TONES[status] ?? "neutral"} label={status ? status.charAt(0).toUpperCase() + status.slice(1) : msg("unknown")} />
										<RowChevron />
									</Cell>
								</DirectoryRow>
							);
						})
					)}
				</DirectoryCard>
			</div>
			<div data-panel="footer">
				<Pagination
					pagination={pagination}
					onPageChange={next => setPage(next)}
					disabled={loading}
				/>
				<div className="footer-actions">
					<Button title={downloading ? "…" : msg("downloadCsv")} variant="secondarySmall" disabled={downloading || !items.length} onClick={() => void downloadCsv()} />
					<Button title={msg("refresh")} variant="primarySmall" disabled={loading} onClick={() => setRevision(value => value + 1)} />
				</div>
			</div>
		</Layout>
	);
};
